import { useMemo, useContext } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { InsuranceReportContext } from '../dental-insurance-eligibility-report';
import { titleCaseWrapper, filterSummaryData } from '../helpers';
import { SummaryDataType } from '../types';
import { infoTitle, sectionContent, alignHorizontal, flexRowWrapItems } from './common-styles';

type SummaryProps = {
  data: SummaryDataType[];
  expanded?: boolean;
  applyFilters?: boolean;
  noDataFoundMessage?: string;
};

export const Summary = ({ data, applyFilters = false, expanded = false, noDataFoundMessage = '' }: SummaryProps) => {
  const { t } = useTranslation('insuranceEligibilityReport');
  const { filters, showNetworkFilter } = useContext(InsuranceReportContext);

  const filteredSummaryData = useMemo(
    () => filterSummaryData({ data: data ?? [], filters, showNetworkFilter, applyFilters }),
    [data, applyFilters, filters, showNetworkFilter]
  );

  if (filteredSummaryData && filteredSummaryData.length > 0) {
    return (
      <div css={[flexRowWrapItems, expanded && cardExpandedStyle]}>
        {filteredSummaryData?.map(({ title, subTitle, labels, predecessor, successor, ...rest }, index: number) => (
          <div
            css={{ margin: theme.spacing(3, 3, 0, 0) }}
            key={`summary-card-${title ?? ''}-${subTitle ?? ''}-${index}`}
          >
            <Text>{titleCaseWrapper(title)}</Text>
            {subTitle && <Text css={infoTitle}>{titleCaseWrapper(subTitle)}</Text>}
            <span css={alignHorizontal}>
              {labels.map((key) => (
                <div css={labelContainer} key={`summary-label-container-${key}-${rest[key]}-${index}`}>
                  <Text css={sectionContent}>{`${predecessor ?? ''}${rest[key]}${successor ?? ''}`}</Text>
                  <Text css={infoTitle}>{titleCaseWrapper(key)}</Text>
                </div>
              ))}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (!!data.length && !filteredSummaryData.length) {
    return <Text>{noDataFoundMessage || t('No data found')}</Text>;
  }

  return null;
};

const labelContainer = css`
  padding: ${theme.spacing(0, 3, 0, 0)};
  display: inherit;
  flex-direction: column;
  align-items: center;
`;

const cardExpandedStyle = css`
  height: fit-content;
  max-height: ${theme.spacing(30)};
  overflow: hidden;
`;
